/**
 * Super Admin platform dashboard — overview, pump registry, billing, and alerts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Sidebar_Progress from '../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../LoginHeaderOperational';
import { API_URL } from '../../../constant/API_Settings';
import { fetchAdminOverview, fetchAdminPumps, fetchSystemMonitor } from './adminApi';
import { ErrorCountLink } from './AdminLedgerErrors.component.jsx';
import { fetchPlatformSuperAdminProfile } from '../../../utils/platformAuth';
import './admin-dashboard.css';

const QUICK_ACTIONS = [
  { to: '/admin-support', label: 'Support Tickets', desc: 'View and reply to pump tickets', tone: 'danger' },
  { to: '/admin-alerts', label: 'Alerts Center', desc: 'Review platform issues', tone: 'danger' },
  { to: '/admin-system-monitoring', label: 'System Monitoring', desc: 'Servers, queues, crons & alerts', tone: 'danger' },
  { to: '/admin-ledger-errors', label: 'Ledger Errors', desc: 'BS vs PL calculation mismatches', tone: 'danger' },
  { to: '/admin-tank-stock-errors', label: 'Tank Stock Errors', desc: 'Nozzle-close race under-deductions', tone: 'danger' },
  { to: '/admin-analytics', label: 'Platform Analytics', desc: 'Charts and trends', tone: 'primary' },
  { to: '/admin-business-insights', label: 'Business Insights', desc: 'Modular profit & staffing KPIs', tone: 'success' },
  { to: '/admin-subscription-management', label: 'Subscriptions', desc: 'Plans and billing', tone: 'primary' },
  { to: '/admin-payment-management', label: 'Payments', desc: 'Razorpay & gateway transactions', tone: 'primary' },
  { to: '/admin-user-management', label: 'User Management', desc: 'Pump users and access', tone: 'primary' },
  { to: '/admin-audit-log', label: 'Audit Log', desc: 'Admin activity trail', tone: 'neutral' },
  { to: '/admin-invoice-settings', label: 'Invoice Settings', desc: 'Billing templates', tone: 'neutral' },
  { to: '/admin-role-list', label: 'Roles', desc: 'Role definitions', tone: 'neutral' },
  { to: '/permission', label: 'Permissions', desc: 'Access control', tone: 'neutral' },
  { to: '/pump_name', label: 'Set Pump Context', desc: 'Switch active pump', tone: 'neutral' },
];

function formatInr(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN')}`;
}

function platformHealthScore(summary) {
  const total = Number(summary.total_pumps) || 1;
  const active = Number(summary.active_pumps) || 0;
  const expired = Number(summary.expired_subscriptions) || 0;
  const expiring = Number(summary.expiring_soon_subscriptions) || 0;
  const alerts = Number(summary.open_alerts) || 0;
  const complaints = Number(summary.open_complaints) || 0;

  let score = Math.round((active / total) * 100);
  score -= expired * 4;
  score -= expiring * 2;
  score -= alerts * 2;
  score -= complaints * 3;

  return Math.max(0, Math.min(100, score));
}

function healthTone(score) {
  if (score >= 75) return 'ok';
  if (score >= 50) return 'warn';
  return 'bad';
}

const AdminDashBoard = (props) => {
  const history = useHistory();
  const [overview, setOverview] = useState(null);
  const [pumps, setPumps] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [serverHealth, setServerHealth] = useState(null);
  const skipSearchDebounce = useRef(true);

  const loadDashboard = useCallback((page = 1, query = '', initial = false) => {
    if (initial) {
      setLoading(true);
    } else {
      setTableLoading(true);
    }

    Promise.all([fetchAdminOverview(), fetchAdminPumps(query, page)])
      .then(([overviewData, pumpData]) => {
        setOverview(overviewData);
        setPumps(pumpData.data || []);
        setPagination(pumpData.pagination || {});
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setLoading(false);
        setTableLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (profile) {
          loadDashboard(1, '', true);
          fetchSystemMonitor()
            .then((payload) => setServerHealth(payload?.overall?.overall || 'ok'))
            .catch(() => setServerHealth(null));
        } else {
          history.goBack();
        }
      })
      .catch(() => history.goBack());
  }, [history, loadDashboard]);

  useEffect(() => {
    if (loading) return undefined;
    if (skipSearchDebounce.current) {
      skipSearchDebounce.current = false;
      return undefined;
    }

    const timer = setTimeout(() => {
      loadDashboard(1, search);
    }, 350);

    return () => clearTimeout(timer);
  }, [search, loading, loadDashboard]);

  function handlePage(page) {
    loadDashboard(page, search);
  }

  function subscriptionBadge(sub) {
    if (!sub) return <span className="admin-badge expired">No plan</span>;
    if (!sub.is_active) return <span className="admin-badge expired">Expired</span>;
    if (sub.days_remaining <= 30) return <span className="admin-badge expired">Expires in {sub.days_remaining}d</span>;
    return <span className="admin-badge active">Active</span>;
  }

  const summary = overview?.summary || {};
  const billing = overview?.billing || {};
  const alerts = overview?.recent_alerts || [];
  const support = overview?.support || {};
  const openComplaints = support.recent_open_complaints || [];
  const slaBreaches = support.recent_sla_breaches || [];
  const healthScore = useMemo(() => platformHealthScore(summary), [summary]);
  const healthClass = healthTone(healthScore);

  const kpiGroups = [
    {
      title: 'Platform',
      cards: [
        { label: 'Total Pumps', value: summary.total_pumps || 0, tone: 'default' },
        { label: 'Active Pumps', value: summary.active_pumps || 0, tone: 'success' },
        { label: 'Inactive Pumps', value: summary.inactive_pumps || 0, tone: 'danger' },
        { label: 'Pump Users', value: summary.total_pump_users || 0, tone: 'default' },
        {
          label: 'Server health',
          value: serverHealth === 'critical' ? 'Critical' : serverHealth === 'warning' ? 'Warning' : serverHealth ? 'Healthy' : '—',
          tone: serverHealth === 'critical' ? 'danger' : serverHealth === 'warning' ? 'warning' : 'success',
          to: '/admin-system-monitoring',
        },
      ],
    },
    {
      title: 'Subscriptions',
      cards: [
        { label: 'Expired', value: summary.expired_subscriptions || 0, tone: 'danger' },
        { label: 'Expiring in 30 Days', value: summary.expiring_soon_subscriptions || 0, tone: 'warning' },
        { label: 'Open Alerts', value: summary.open_alerts || 0, tone: 'warning', to: '/admin-alerts' },
        { label: 'Ledger Errors', value: summary.ledger_mismatch_errors || 0, tone: 'danger', to: '/admin-ledger-errors' },
        { label: 'Tank Stock Gaps', value: summary.tank_stock_race_open || 0, tone: 'danger', to: '/admin-tank-stock-errors' },
      ],
    },
    {
      title: 'Billing',
      cards: [
        { label: 'Total Receipts', value: formatInr(billing.total_received), tone: 'success' },
        { label: 'Payment Due', value: formatInr(billing.total_due), tone: 'danger' },
      ],
    },
    {
      title: 'Support Tickets',
      cards: [
        { label: 'Open Complaints', value: summary.open_complaints || 0, tone: 'danger' },
        { label: 'Open Tickets', value: summary.open_support_tickets || 0, tone: 'warning' },
        { label: 'In Progress', value: support.in_progress || 0, tone: 'warning' },
        { label: 'SLA Breached', value: support.sla_breached || 0, tone: 'danger' },
      ],
    },
  ];

  function complaintStatusClass(status) {
    if (status === 'open') return 'open';
    if (status === 'in_progress') return 'progress';
    if (status === 'waiting_customer') return 'waiting';
    return 'default';
  }

  const pageWindow = useMemo(() => {
    const current = pagination.current_page || 1;
    const last = pagination.last_page || 1;
    const start = Math.max(1, current - 2);
    const end = Math.min(last, current + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [pagination]);

  return (
    <div className="main-container page-wrap admin-platform admin-dash-home">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={18} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Super Admin Control Center" />
          <div className="container pb-3">
            {loading ? (
              <div className="admin-dash-home__loading">
                <div className="admin-dash-skeleton admin-dash-skeleton--hero" />
                <div className="admin-dash-kpi-groups">
                  {[1, 2, 3, 4].map((g) => (
                    <div key={g} className="admin-dash-kpi-group">
                      <div className="admin-dash-skeleton admin-dash-skeleton--title" />
                      <div className="admin-dash-kpi-row">
                        {[1, 2, 3].map((c) => (
                          <div key={c} className="admin-dash-skeleton admin-dash-skeleton--card" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <header className="admin-dash-home__header">
                  <div>
                    <h2 className="admin-dash-home__title">Platform overview</h2>
                    <p className="admin-dash-home__subtitle">
                      {summary.total_pumps || 0} pumps registered · {summary.active_pumps || 0} active today
                    </p>
                  </div>
                  <div className={`admin-dash-health admin-dash-health--${healthClass}`}>
                    <span className="admin-dash-health__score">{healthScore}</span>
                    <span className="admin-dash-health__label">Health score</span>
                  </div>
                </header>

                <div className="admin-dash-kpi-groups">
                  {kpiGroups.map((group) => (
                    <section key={group.title} className="admin-dash-kpi-group">
                      <h6 className="admin-dash-kpi-group__title">{group.title}</h6>
                      <div className="admin-dash-kpi-row">
                        {group.cards.map((card) => {
                          const cardClass = `admin-dash-kpi-card admin-dash-kpi-card--${card.tone}`;
                          const inner = (
                            <>
                              <p className="admin-dash-kpi-card__value">{card.value}</p>
                              <p className="admin-dash-kpi-card__label">{card.label}</p>
                            </>
                          );
                          return card.to ? (
                            <Link key={card.label} to={card.to} className={`${cardClass} admin-dash-kpi-card--link`}>
                              {inner}
                            </Link>
                          ) : (
                            <div key={card.label} className={cardClass}>
                              {inner}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="row g-3">
                  <div className="col-lg-7">
                    <div className="admin-panel admin-dash-panel">
                      <div className="admin-dash-panel__head">
                        <div>
                          <h5 className="mb-0">Pump registry</h5>
                          <p className="admin-dash-panel__meta mb-0">
                            {pagination.total || pumps.length} pump{(pagination.total || pumps.length) !== 1 ? 's' : ''} listed
                          </p>
                        </div>
                        <div className="admin-dash-search-wrap">
                          <input
                            type="text"
                            className="form-control form-control-sm admin-dash-search"
                            placeholder="Search by name, email, or phone..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                          {tableLoading && <span className="admin-dash-search__spinner" aria-hidden="true" />}
                        </div>
                      </div>

                      <div className={`admin-dash-table-wrap ${tableLoading ? 'is-loading' : ''}`}>
                        {pumps.length === 0 ? (
                          <div className="admin-dash-empty">
                            <p className="mb-1"><strong>No pumps found</strong></p>
                            <p className="text-muted small mb-0">
                              {search ? 'Try a different search term.' : 'No pumps are registered yet.'}
                            </p>
                          </div>
                        ) : (
                          <div className="table-responsive">
                            <table className="table table-hover admin-pump-table admin-dash-pump-table">
                              <thead>
                                <tr>
                                  <th>Pump</th>
                                  <th>Users</th>
                                  <th>Subscription</th>
                                  <th>Last active</th>
                                  <th>Errors</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {pumps.map((pump) => (
                                  <tr key={pump.id}>
                                    <td>
                                      <div className="admin-dash-pump-name">{pump.pump_name}</div>
                                      <div className="admin-dash-pump-meta">{pump.pump_phone || pump.pump_email || '—'}</div>
                                      <span className={`admin-badge ${pump.status === 1 ? 'active' : 'inactive'}`}>
                                        {pump.status === 1 ? 'Active' : 'Inactive'}
                                      </span>
                                      {pump.maintenance_mode ? (
                                        <span className="admin-badge inactive ms-1" title={pump.global_maintenance_mode ? 'Global maintenance' : 'Pump maintenance'}>
                                          Maintenance
                                        </span>
                                      ) : null}
                                    </td>
                                    <td><span className="admin-dash-pill">{pump.user_count}</span></td>
                                    <td>
                                      {subscriptionBadge(pump.subscription)}
                                      {pump.subscription && (
                                        <div className="admin-dash-pump-meta mt-1">
                                          {pump.subscription.plan_name} · ends {pump.subscription.end_date}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      <div>{pump.last_active_date || '—'}</div>
                                      <div className="admin-dash-pump-meta">{pump.last_user_name || ''}</div>
                                    </td>
                                    <td>
                                      <ErrorCountLink pumpId={pump.id} count={pump.error_count} />
                                    </td>
                                    <td className="text-end">
                                      <Link to={`/admin-pump/${pump.id}`} className="btn btn-sm btn-primary admin-dash-manage-btn">
                                        Manage
                                      </Link>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {pagination.last_page > 1 && (
                        <div className="admin-dash-pagination">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={pagination.current_page <= 1}
                            onClick={() => handlePage(pagination.current_page - 1)}
                          >
                            Previous
                          </button>
                          {pageWindow.map((page) => (
                            <button
                              key={page}
                              type="button"
                              className={`btn btn-sm ${page === pagination.current_page ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => handlePage(page)}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={pagination.current_page >= pagination.last_page}
                            onClick={() => handlePage(pagination.current_page + 1)}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-5">
                    <div className="admin-panel admin-dash-panel">
                      <div className="admin-dash-panel__head">
                        <h5 className="mb-0">
                          Open complaints
                          {(summary.open_complaints || 0) > 0 && (
                            <span className="admin-dash-count-badge">{summary.open_complaints}</span>
                          )}
                        </h5>
                        <Link to="/admin-support" className="small">Support Tickets</Link>
                      </div>
                      <p className="admin-dash-panel__meta">
                        Customer complaints awaiting review or resolution.
                      </p>
                      {openComplaints.length === 0 ? (
                        <div className="admin-dash-empty admin-dash-empty--compact">
                          <p className="text-muted mb-0">No open complaints right now.</p>
                        </div>
                      ) : (
                        openComplaints.map((ticket) => (
                          <div key={ticket.id} className="admin-dash-complaint-item">
                            <div className="admin-dash-complaint-item__head">
                              <strong className="admin-dash-complaint-item__subject">{ticket.subject}</strong>
                              <span className={`admin-dash-ticket-status admin-dash-ticket-status--${complaintStatusClass(ticket.status)}`}>
                                {ticket.status_label || ticket.status}
                              </span>
                            </div>
                            <div className="admin-dash-complaint-item__meta">
                              <span>{ticket.ticket_no}</span>
                              <span>·</span>
                              <span>{ticket.pump_name}</span>
                              {ticket.priority_label && (
                                <>
                                  <span>·</span>
                                  <span className={`admin-dash-priority admin-dash-priority--${ticket.priority}`}>
                                    {ticket.priority_label}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="admin-dash-complaint-item__foot">
                              <span className="small text-muted">
                                {ticket.user_name || 'Customer'}
                                {ticket.updated_at ? ` · ${ticket.updated_at}` : ''}
                              </span>
                              <Link to="/admin-support" className="small">Respond →</Link>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="admin-panel admin-dash-panel">
                      <div className="admin-dash-panel__head">
                        <h5 className="mb-0">
                          SLA breached
                          {(support.sla_breached || 0) > 0 && (
                            <span className="admin-dash-count-badge">{support.sla_breached}</span>
                          )}
                        </h5>
                        <Link to="/admin-support" className="small">Support Tickets</Link>
                      </div>
                      <p className="admin-dash-panel__meta">
                        Tickets past resolution SLA (High 24h / Medium 72h / Low 120h).
                      </p>
                      {slaBreaches.length === 0 ? (
                        <div className="admin-dash-empty admin-dash-empty--compact">
                          <p className="text-muted mb-0">No SLA breaches right now.</p>
                        </div>
                      ) : (
                        slaBreaches.map((ticket) => (
                          <div key={`sla-${ticket.id}`} className="admin-dash-complaint-item">
                            <div className="admin-dash-complaint-item__head">
                              <strong className="admin-dash-complaint-item__subject">{ticket.subject}</strong>
                              <span className="admin-dash-ticket-status admin-dash-ticket-status--open">SLA</span>
                            </div>
                            <div className="admin-dash-complaint-item__meta">
                              <span>{ticket.ticket_no}</span>
                              <span>·</span>
                              <span>{ticket.pump_name}</span>
                              <span>·</span>
                              <span className={`admin-dash-priority admin-dash-priority--${ticket.priority}`}>
                                {ticket.priority_label}
                              </span>
                            </div>
                            <div className="admin-dash-complaint-item__foot">
                              <span className="small text-danger">
                                Due {ticket.sla?.resolution_due_at || '—'}
                                {ticket.sla?.resolution_overdue_hours
                                  ? ` · ${ticket.sla.resolution_overdue_hours}h overdue`
                                  : ''}
                              </span>
                              <Link to="/admin-support" className="small">Open →</Link>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="admin-panel admin-dash-panel">
                      <div className="admin-dash-panel__head">
                        <h5 className="mb-0">
                          Priority alerts
                          {alerts.length > 0 && (
                            <span className="admin-dash-count-badge">{summary.open_alerts || alerts.length}</span>
                          )}
                        </h5>
                        <Link to="/admin-alerts" className="small">View all</Link>
                      </div>
                      <p className="admin-dash-panel__meta">
                        Subscription issues, inactive pumps, and items needing follow-up.
                      </p>
                      {alerts.length === 0 ? (
                        <div className="admin-dash-empty admin-dash-empty--compact">
                          <p className="text-muted mb-0">No open alerts right now.</p>
                        </div>
                      ) : (
                        alerts.map((alert, idx) => (
                          <div key={idx} className={`admin-alert-item admin-dash-alert ${alert.severity}`}>
                            <strong>{alert.title}</strong>
                            <div className="small mt-1">{alert.message}</div>
                            {alert.pump_id && (
                              <Link to={`/admin-pump/${alert.pump_id}`} className="small d-inline-block mt-2">
                                Open pump →
                              </Link>
                            )}
                          </div>
                        ))
                      )}
                      <Link to="/admin-pumps-details" className="btn btn-outline-secondary btn-sm mt-2">
                        View pump activity
                      </Link>
                    </div>

                    <div className="admin-panel admin-dash-panel">
                      <h5 className="mb-2">Quick actions</h5>
                      <div className="admin-dash-action-grid">
                        {QUICK_ACTIONS.map((action) => (
                          <Link
                            key={action.to}
                            to={action.to}
                            className={`admin-dash-action-tile admin-dash-action-tile--${action.tone}`}
                          >
                            <span className="admin-dash-action-tile__label">{action.label}</span>
                            <span className="admin-dash-action-tile__desc">{action.desc}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashBoard;
